/**
 * Bulk registration — one division per picked event, and the organizer's
 * registration questions, as an accordion: one event open at a time.
 *
 * Picking a division closes that event (its summary shows the choice, tap
 * to change) and opens the next unfinished one. An event whose questions no
 * other picked event asks keeps them in its own step, with a Next button.
 * Questions several events ask exactly alike are answered once, in a last
 * "Questions" step, and every one of those events gets the answers.
 *
 * Divisions come from the same endpoint the single flow uses, grouped by
 * sport / gender when their names say so (divisionGroups). An event with
 * exactly one open division is picked automatically; full or closed
 * divisions show but can't be chosen. Questions come from the same endpoint
 * Confirm uses (empty where the embed doesn't ask, e.g. GFG); the server
 * checks every answer again before registering anything.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { EmbedClient, DivisionResponse, RegistrationQuestion } from '@/api/embed';
import { useFlow } from '@/state/FlowContext';
import { useConfig } from '@/state/vocab';
import { shortDate } from './bulkFormat';
import { checkAnswers, QuestionField } from './ConfirmPage';
import { groupDivisions } from './divisionGroups';
import './Bulk.css';

interface Props {
  client: EmbedClient;
}

const QUESTIONS_STEP = 'questions';
/** Long enough to see the choice land before the step closes. */
const ADVANCE_DELAY_MS = 220;

type Answers = Record<string, string>;

export function BulkDivisionsPage({ client }: Props) {
  const navigate = useNavigate();
  const { selectedTeam, bulkEvents, bulkDivisions, setBulkDivision, bulkAnswers, setBulkAnswers } = useFlow();
  const config = useConfig();
  const asksQuestions = config?.theme !== 'gfg';
  const [divisions, setDivisions] = useState<Record<string, DivisionResponse[]> | null>(null);
  const [questions, setQuestions] = useState<Record<string, RegistrationQuestion[]> | null>(asksQuestions ? null : {});
  const [errors, setErrors] = useState<Record<string, Answers>>({});
  const [error, setError] = useState<string | null>(null);
  // The open step (an event id or QUESTIONS_STEP; null = all closed), and
  // the steps the coach has finished. Set once everything has loaded.
  const [openId, setOpenId] = useState<string | null>(null);
  const [done, setDone] = useState<Set<string>>(new Set());
  const [started, setStarted] = useState(false);

  // Price the picks in the background as divisions are chosen (once the
  // clicking settles), so Review opens on that request already answered
  // and its ways to pay are ready; same events and divisions as Review.
  useEffect(() => {
    if (!selectedTeam || bulkEvents.length === 0) return;
    const t = window.setTimeout(() => {
      client
        .previewBatch(selectedTeam.id, bulkEvents.map((e) => ({ event_id: e.id, division: bulkDivisions[e.id] })))
        .catch(() => { /* Review asks again */ });
    }, 400);
    return () => window.clearTimeout(t);
  }, [client, selectedTeam, bulkEvents, bulkDivisions]);

  useEffect(() => {
    if (!selectedTeam || bulkEvents.length === 0) {
      navigate('/bulk/events', { replace: true });
      return;
    }
    let cancelled = false;
    Promise.all(bulkEvents.map((e) => client.getDivisions(e.id).then((d) => [e.id, d] as const)))
      .then((pairs) => {
        if (cancelled) return;
        setDivisions(Object.fromEntries(pairs));
        for (const [eventId, list] of pairs) {
          const open = list.filter((d) => !d.is_full && !d.is_closed);
          if (open.length === 1 && !bulkDivisions[eventId]) setBulkDivision(eventId, open[0].name);
        }
      })
      .catch(() => { if (!cancelled) setError('Could not load divisions. Please try again.'); });
    return () => { cancelled = true; };
    // Load once per picked set; bulkDivisions is read only for the auto-pick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, selectedTeam, bulkEvents, navigate]);

  // Each event's questions. A failed lookup asks nothing here; the server
  // still checks, and the review step shows what it says.
  useEffect(() => {
    if (!asksQuestions || bulkEvents.length === 0) return;
    let cancelled = false;
    Promise.all(bulkEvents.map((e) =>
      client.getRegistrationQuestions(e.id).then((q) => [e.id, q] as const).catch(() => [e.id, []] as const),
    )).then((pairs) => { if (!cancelled) setQuestions(Object.fromEntries(pairs)); });
    return () => { cancelled = true; };
  }, [client, asksQuestions, bulkEvents]);

  // Events asking exactly the same questions (same wording, type, choices,
  // limits and required flags; ids differ per event) share one set of
  // answers. Each set has a "lead" whose answers are shown and copied to the
  // rest: the event already holding answers (carried from Confirm), else the
  // first. Decided once per loaded question set.
  const sets = useMemo(() => {
    const signature = (qs: RegistrationQuestion[]) =>
      JSON.stringify(qs.map((q) => [q.type, q.label.trim().toLowerCase(), q.required, q.options, q.min, q.max, (q.help ?? '').trim()]));
    const bySig = new Map<string, string[]>();
    for (const e of bulkEvents) {
      const qs = questions?.[e.id] ?? [];
      if (qs.length === 0) continue;
      const k = signature(qs);
      bySig.set(k, [...(bySig.get(k) ?? []), e.id]);
    }
    return [...bySig.values()].map((ids) => ({
      ids,
      lead: ids.find((id) => Object.keys(bulkAnswers[id] ?? {}).length > 0) ?? ids[0],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkEvents, questions]);
  const sharedSets = sets.filter((s) => s.ids.length > 1);
  const ownSetOf = (eventId: string) => sets.find((s) => s.ids.length === 1 && s.ids[0] === eventId) ?? null;

  const loaded = !!divisions && !!questions;
  const listFor = (eventId: string) => divisions?.[eventId] ?? [];
  const ownQuestions = (eventId: string) => (ownSetOf(eventId) ? questions?.[eventId] ?? [] : []);
  // Steps: events with a division to pick or questions of their own.
  const steps = bulkEvents.filter((e) => listFor(e.id).length > 0 || ownQuestions(e.id).length > 0);
  const divisionSet = (eventId: string, divisionsNow = bulkDivisions) =>
    listFor(eventId).length === 0 || !!divisionsNow[eventId];
  const ownAnswered = (eventId: string) =>
    Object.keys(checkAnswers(ownQuestions(eventId), bulkAnswers[eventId] ?? {})).length === 0;
  const sharedAnswered = () =>
    sharedSets.every((s) => Object.keys(checkAnswers(questions?.[s.lead] ?? [], bulkAnswers[s.lead] ?? {})).length === 0);

  /** The next step to open once `finished` steps are done, or null. */
  const nextStep = (finished: Set<string>) => {
    const ev = steps.find((e) => !finished.has(e.id));
    if (ev) return ev.id;
    return sharedSets.length && !finished.has(QUESTIONS_STEP) ? QUESTIONS_STEP : null;
  };

  // Once everything is loaded: steps already complete (an auto-picked single
  // division, a choice kept from going back, answers carried from Confirm)
  // start closed, and the first unfinished one opens.
  useEffect(() => {
    if (!loaded || started) return;
    const finished = new Set(steps.filter((e) => divisionSet(e.id) && ownAnswered(e.id)).map((e) => e.id));
    if (sharedSets.length && sharedAnswered()) finished.add(QUESTIONS_STEP);
    setDone(finished);
    setOpenId(nextStep(finished));
    setStarted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, started]);


  if (error) return <div role="alert" className="tn-bulk__error">{error}</div>;
  if (!loaded) return <div className="tn-bulk__loading">Loading divisions…</div>;

  /** A set's lead answers, re-keyed to another event's (identical) questions. */
  const answersFor = (lead: string, other: string, answers: Answers): Answers => {
    const from = questions[lead] ?? [];
    const to = questions[other] ?? [];
    return Object.fromEntries(to.map((q, i) => [q.id, answers[from[i]?.id] ?? '']).filter(([, v]) => v !== ''));
  };

  const finish = (stepId: string) => {
    const finished = new Set(done).add(stepId);
    setDone(finished);
    setOpenId(nextStep(finished));
  };

  // The step nothing follows: its picks and answers are confirmed by
  // Continue, so it has no Next and doesn't fold away (with one event, that
  // would collapse the whole screen to a summary line).
  const lastStepId = sharedSets.length ? QUESTIONS_STEP : steps[steps.length - 1]?.id;

  const pick = (eventId: string, name: string) => {
    setBulkDivision(eventId, name);
    // With no questions of its own, choosing the division finishes the step.
    if (ownQuestions(eventId).length === 0 && eventId !== lastStepId) {
      window.setTimeout(() => finish(eventId), ADVANCE_DELAY_MS);
    }
  };

  const nextFromOwnQuestions = (eventId: string) => {
    const errs = checkAnswers(ownQuestions(eventId), bulkAnswers[eventId] ?? {});
    setErrors({ ...errors, [eventId]: errs });
    if (!divisionSet(eventId) || Object.keys(errs).length) return;
    finish(eventId);
  };

  const answerField = (eventId: string, followers: string[]) => (q: RegistrationQuestion) => {
    const answers = bulkAnswers[eventId] ?? {};
    return (
      <QuestionField
        key={q.id}
        q={q}
        idPrefix={`${eventId}-`}
        value={answers[q.id] ?? ''}
        error={errors[eventId]?.[q.id]}
        onChange={(v) => {
          const next = { ...answers, [q.id]: v };
          setBulkAnswers(eventId, next);
          for (const other of followers) setBulkAnswers(other, answersFor(eventId, other, next));
          if (errors[eventId]?.[q.id]) {
            const rest = { ...errors[eventId] };
            delete rest[q.id];
            setErrors({ ...errors, [eventId]: rest });
          }
        }}
      />
    );
  };

  const missing = steps.find((e) => !divisionSet(e.id));

  const onContinue = () => {
    // Shared questions: every event in a set gets its lead's answers.
    for (const s of sharedSets) {
      for (const other of s.ids.filter((id) => id !== s.lead)) {
        setBulkAnswers(other, answersFor(s.lead, other, bulkAnswers[s.lead] ?? {}));
      }
    }
    // Check every step's questions (the lead stands for its set); open the
    // first one with a problem.
    const found: Record<string, Answers> = {};
    for (const s of sets) {
      const errs = checkAnswers(questions[s.lead] ?? [], bulkAnswers[s.lead] ?? {});
      if (Object.keys(errs).length) found[s.lead] = errs;
    }
    setErrors(found);
    const firstOwn = steps.find((e) => found[e.id] && ownSetOf(e.id));
    if (firstOwn) return setOpenId(firstOwn.id);
    if (sharedSets.some((s) => found[s.lead])) return setOpenId(QUESTIONS_STEP);
    navigate('/bulk/review');
  };

  const eventName = (id: string) => bulkEvents.find((e) => e.id === id)?.name ?? '';
  const listNames = (ids: string[]) =>
    ids.length <= 2 ? ids.map(eventName).join(' and ') : `${ids.slice(0, -1).map(eventName).join(', ')} and ${eventName(ids[ids.length - 1])}`;

  return (
    <div className="tn-bulk">
      <h1 className="tn-bulk__title">Choose divisions</h1>
      <p className="tn-bulk__subtitle">
        {sets.length ? 'One division per event, and the organizer’s questions.' : 'One division per event.'}
      </p>

      <div className="tn-bulk__steps">
        {steps.map((e, i) => {
          const open = openId === e.id;
          const complete = done.has(e.id) && divisionSet(e.id);
          const chosen = bulkDivisions[e.id];
          const ownQs = ownQuestions(e.id);
          const where = [shortDate(e.event_date), e.venue_name || e.location].filter(Boolean).join(' · ');
          const summary = open ? where : complete ? chosen || 'Questions answered' : 'Choose a division';
          return (
            <Step
              key={e.id}
              id={e.id}
              open={open}
              complete={complete && !open}
              title={e.name}
              counter={steps.length > 1 ? `${i + 1} of ${steps.length}` : null}
              summary={summary}
              onOpen={() => setOpenId(e.id)}
            >
              {groupDivisions(listFor(e.id)).map((group) => (
                <div key={group.label ?? 'all'} className="tn-bulk__div-group">
                  {group.label ? <span className="tn-bulk__div-label">{group.label}</span> : null}
                  <ul className="tn-bulk__group" role="radiogroup" aria-label={group.label ? `${e.name}, ${group.label}` : e.name}>
                    {group.items.map(({ division: d, display }) => {
                      const unavailable = d.is_full || d.is_closed;
                      const on = chosen === d.name;
                      return (
                        <li key={d.id}>
                          <label className={`tn-bulk__row${on ? ' tn-bulk__row--on' : ''}${unavailable ? ' tn-bulk__row--off' : ''}`}>
                            <input
                              type="radio"
                              className="tn-bulk__check"
                              name={`division-${e.id}`}
                              checked={on}
                              disabled={unavailable}
                              // onClick, not onChange: choosing the same division
                              // again (after reopening) still moves on.
                              onClick={() => pick(e.id, d.name)}
                              onChange={() => {}}
                            />
                            <span className="tn-bulk__row-text">
                              <span className="tn-bulk__row-title">{display}</span>
                            </span>
                            {unavailable ? (
                              <span className="tn-bulk__row-note">{d.is_full ? 'Full' : 'Registration closed'}</span>
                            ) : null}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
              {ownQs.length > 0 ? (
                <>
                  <div className="tn-bulk__questions">{ownQs.map(answerField(e.id, []))}</div>
                  {e.id === lastStepId ? null : <button
                    type="button"
                    className="tn-btn tn-btn--secondary tn-bulk__step-next"
                    disabled={!divisionSet(e.id)}
                    onClick={() => nextFromOwnQuestions(e.id)}
                  >
                    Next
                  </button>}
                </>
              ) : null}
            </Step>
          );
        })}

        {sharedSets.length ? (
          <Step
            id={QUESTIONS_STEP}
            open={openId === QUESTIONS_STEP}
            complete={done.has(QUESTIONS_STEP) && sharedAnswered() && openId !== QUESTIONS_STEP}
            title="Questions"
            counter={null}
            summary={
              openId === QUESTIONS_STEP
                ? sharedSets.length === 1
                  ? `For ${listNames(sharedSets[0].ids)}`
                  : 'Answered once for the events that ask the same.'
                : sharedAnswered()
                  ? 'Answered'
                  : `For ${listNames(sharedSets.flatMap((s) => s.ids))}`
            }
            onOpen={() => setOpenId(QUESTIONS_STEP)}
          >
            {sharedSets.map((s) => (
              <div key={s.lead} className="tn-bulk__questions">
                {sharedSets.length > 1 ? <p className="tn-bulk__shared">For {listNames(s.ids)}</p> : null}
                {(questions[s.lead] ?? []).map(answerField(s.lead, s.ids.filter((id) => id !== s.lead)))}
              </div>
            ))}
          </Step>
        ) : null}
      </div>

      <div className="tn-bulk__actions">
        <button type="button" className="tn-btn tn-btn--primary" disabled={!!missing} onClick={onContinue}>
          {missing ? `Choose a division for ${missing.name}` : 'Continue'}
        </button>
      </div>
    </div>
  );
}

/**
 * One accordion step. The body stays mounted so it can slide open and
 * closed (CSS grid-rows transition); a closed body is inert, so its fields
 * can't take focus.
 */
function Step({
  id, open, complete, title, counter, summary, onOpen, children,
}: {
  id: string;
  open: boolean;
  complete: boolean;
  title: string;
  counter: string | null;
  summary: string;
  onOpen: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className={`tn-bulk__step${open ? ' is-open' : ''}${complete ? ' is-done' : ''}`} data-step={id}>
      <button type="button" className="tn-bulk__step-head" aria-expanded={open} onClick={open ? undefined : onOpen}>
        <span className="tn-bulk__step-text">
          <span className="tn-bulk__step-title">{title}</span>
          <span className="tn-bulk__step-meta">{summary}</span>
        </span>
        {complete ? (
          <span className="tn-bulk__step-change">Change</span>
        ) : open && counter ? (
          <span className="tn-bulk__step-count">{counter}</span>
        ) : null}
      </button>
      <div
        className="tn-bulk__step-body"
        ref={(el) => {
          if (!el) return;
          if (open) el.removeAttribute('inert');
          else el.setAttribute('inert', '');
        }}
      >
        <div className="tn-bulk__step-inner">
          <div className="tn-bulk__step-content">{children}</div>
        </div>
      </div>
    </section>
  );
}
